import { useContext, useEffect, useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import { ChevronDownIcon, CircleCheckIcon, ShieldQuestionIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { TraceDescription, TraceIcon, TraceLine, TraceTitle } from "@/components/ui/trace";
import { AmyQuestionnaire } from "./AmyQuestionnaire";
import { AskContext, answerLabel, isApproval } from "./asking";
import { isEmptyValue } from "./format";
import { MarkdownMessage } from "./MarkdownMessage";
import type { QuestionBlock } from "./projection";
import { approvalTitle } from "./toolLabels";
import { ValueBlock } from "./values";

const firstLine = (markdown: string): string =>
  (markdown.split("\n").find((l) => l.trim()) ?? "").replace(/[*_`#>]/g, "").trim();

/** An answered question, collapsed to one trace line. */
export function AnsweredQuestionLine({ question }: { question: QuestionBlock }) {
  const answer = answerLabel(question);
  const title = isApproval(question)
    ? approvalTitle(question.request.action.toolName, question.askedBy).replace(/\?$/, "")
    : firstLine(question.request.prompt);
  return (
    <TraceLine>
      <TraceIcon>
        <CircleCheckIcon />
      </TraceIcon>
      <TraceTitle>{title}</TraceTitle>
      {answer && <TraceDescription className="max-w-[45%] truncate">→ {answer}</TraceDescription>}
    </TraceLine>
  );
}

/**
 * A question or tool approval Amy (or one of her subagents) is waiting on,
 * answered inline where it was asked — AI Elements Confirmation for
 * approvals; questions render as a questionnaire (AmyQuestionnaire), which
 * also groups several questions parked together.
 */
export function QuestionCard({ question }: { question: QuestionBlock }) {
  const ask = useContext(AskContext);
  const { request } = question;
  const options = useMemo(() => request.options ?? [], [request.options]);
  const delivering = ask.answering[question.id];
  const enabled = ask.canAnswer && !ask.readOnly && question.status === "parked" && !delivering;
  const approval = isApproval(question);

  // Number keys mirror an approval's options when it is the one they target
  // (questionnaires bring their own shortcuts). Never while typing — digits
  // belong to the field then.
  useEffect(() => {
    if (!approval || !enabled || ask.keyboardTarget !== question.id || options.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable)) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > Math.min(options.length, 9)) return;
      e.preventDefault();
      ask.onAnswer(question.id, { optionId: options[n - 1]!.id });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [approval, enabled, ask, question.id, options]);

  if (approval) {
    const generic = /^approve tool call/i.test(request.prompt.trim());
    return (
      <Item variant="outline" size="sm" className="w-full items-start rounded-lg border-none bg-input/20 p-3 pr-4">
        <ItemMedia variant="icon" className="border-none size-10 rounded-md">
          <ShieldQuestionIcon />
        </ItemMedia>
        <ItemContent className="min-w-0">
          <ItemTitle>{approvalTitle(request.action.toolName, question.askedBy)}</ItemTitle>
          {!generic && (
            <ItemDescription className="line-clamp-none">
              <MarkdownMessage content={request.prompt} />
            </ItemDescription>
          )}
          {!isEmptyValue(request.action.input) && (
            <Collapsible>
              <CollapsibleTrigger className="group flex cursor-pointer items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
                <ChevronDownIcon className="size-3 transition-transform group-data-[state=open]:rotate-180" />
                Details
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <ValueBlock label="Input" value={request.action.input} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </ItemContent>
        <ItemActions className="self-start mt-1">
          {/* Confirmation pattern: the primary (approve) action sits last. */}
          {(options.length ? [...options].sort((a, b) => Number(a.id === "approve") - Number(b.id === "approve")) : [
            { id: "deny", label: "Deny" },
            { id: "approve", label: "Approve" },
          ]).map((o) => (
            <Button
              key={o.id}
              size="sm"
              variant={o.id === "approve" ? "default" : o.style === "danger" ? "destructive" : "secondary"}
              disabled={!enabled}
              onClick={() => ask.onAnswer(question.id, { optionId: o.id })}
            >
              {delivering?.optionId === o.id && <Spinner />}
              {o.label}
            </Button>
          ))}
        </ItemActions>
      </Item>
    );
  }

  return <AmyQuestionnaire questions={[question]} />;
}
